<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.ENFORCE.4 — when an account's security policy refuses the
// current session (SSO required, a second step required, or the session is too
// old / was signed out by an administrator), the API answers 403 with an
// error_code and APIHelper raises a `kaption-security-reauth` window event.
// This overlay explains it and sends the person to sign in again (straight to
// the account's IdP when it requires SSO). Nothing renders otherwise.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Auth from 'dashboard/api/auth';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api';

const { t } = useI18n();
const router = useRouter();
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const state = ref(null);

const onReauth = event => {
  const detail = event.detail || {};
  if (
    detail.accountId &&
    Number(detail.accountId) !== Number(currentAccountId.value)
  )
    return;
  state.value = detail;
};

onMounted(() => window.addEventListener('kaption-security-reauth', onReauth));
onBeforeUnmount(() =>
  window.removeEventListener('kaption-security-reauth', onReauth)
);

const account = computed(() => {
  const accounts = currentUser.value?.accounts || [];
  return accounts.find(a => Number(a.id) === Number(currentAccountId.value));
});
const otherAccount = computed(() =>
  (currentUser.value?.accounts || []).find(
    a => Number(a.id) !== Number(currentAccountId.value) && a.active_at !== null
  )
);
const accountName = computed(
  () =>
    state.value?.sso?.account_name ||
    account.value?.name ||
    t('KAPTION_SECURITY.REAUTH.THIS_ACCOUNT')
);
const provider = computed(() => state.value?.sso?.provider_label || 'SSO');

const copy = computed(() => {
  const code = state.value?.code;
  const vars = { account: accountName.value, provider: provider.value };
  if (code === 'sso_required')
    return {
      title: t('KAPTION_SECURITY.REAUTH.SSO_TITLE', vars),
      desc: t('KAPTION_SECURITY.REAUTH.SSO_DESC', vars),
    };
  if (code === 'sso_required_second_step')
    return {
      title: t('KAPTION_SECURITY.REAUTH.SECOND_STEP_TITLE', vars),
      desc: t('KAPTION_SECURITY.REAUTH.SECOND_STEP_DESC', vars),
    };
  return {
    title: t('KAPTION_SECURITY.REAUTH.EXPIRED_TITLE', vars),
    desc: t('KAPTION_SECURITY.REAUTH.EXPIRED_DESC', vars),
  };
});

const signInAgain = async () => {
  const target =
    state.value?.code === 'sso_required' && state.value?.sso?.start_url
      ? state.value.sso.start_url
      : '/app/login';
  try {
    await Auth.logout();
  } catch (e) {
    // the server may refuse the old session: clear it locally instead
    clearCookiesOnLogout();
  }
  window.location = target;
};

const goToOtherAccount = () => {
  const id = otherAccount.value?.id;
  state.value = null;
  if (id) window.location = `/app/accounts/${id}/dashboard`;
};

const openProfile = () => {
  state.value = null;
  router.push({
    name: 'profile_settings_index',
    params: { accountId: currentAccountId.value },
  });
};
</script>

<template>
  <div
    v-if="state"
    class="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
    data-testid="kaption-security-reauth"
  >
    <div
      class="flex flex-col w-full max-w-md gap-5 p-6 shadow-xl rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
    >
      <div class="flex flex-col gap-2">
        <span
          class="i-lucide-shield-alert size-6 text-n-brand"
          aria-hidden="true"
        />
        <h2 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ copy.title }}
        </h2>
        <p class="m-0 text-sm text-n-slate-11">{{ copy.desc }}</p>
      </div>
      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="h-10 text-sm font-medium text-white rounded-full bg-n-brand"
          @click="signInAgain"
        >
          {{ t('KAPTION_SECURITY.REAUTH.SIGN_IN_AGAIN') }}
        </button>
        <button
          v-if="state.code === 'sso_required_second_step'"
          type="button"
          class="h-10 text-sm font-medium rounded-full text-n-slate-12 outline outline-1 outline-n-weak"
          @click="openProfile"
        >
          {{ t('KAPTION_SECURITY.REAUTH.OPEN_PROFILE') }}
        </button>
        <button
          v-if="otherAccount"
          type="button"
          class="h-10 text-sm text-n-brand"
          @click="goToOtherAccount"
        >
          {{ otherAccount.name }}
        </button>
      </div>
    </div>
  </div>
</template>
